<template>
  <div class="plugin-header" :style="cssVars">
    <button class="ph-back" @click="goBack">
      <i class="fas fa-chevron-left"></i> <span class="ph-back-label">Back to farm</span>
    </button>
    <div class="ph-title-block">
      <span class="ph-icon" v-if="icon">
        <i :class="icon"></i>
      </span>
      <div class="ph-title-text">
        <span class="ph-title">{{ title }}</span>
        <span v-if="subtitle" class="ph-subtitle">{{ subtitle }}</span>
      </div>
    </div>
    <div class="ph-actions">
      <slot name="actions"></slot>
    </div>
  </div>
</template>

<script>
/**
 * PluginHeader — shared chrome rendered at the top of every in-codebase
 * plugin (Raster Analysis, Interpolator, Grid Generator, Translator,
 * Data Joiner, Multivariate Zoning). The bar is fixed at the top with
 * a height of --plugin-header-h (default 56px); plugins are expected
 * to leave that much room for their own absolutely-positioned panels:
 *
 *     #options { top: 56px; height: calc(100% - 56px); }
 *     #map     { top: 56px; height: calc(100% - 56px); }
 *
 * The header gives every plugin the same "Back to farm" affordance and
 * surfaces the plugin name + a short tagline so users know where they
 * are. Plugin-specific buttons can land in the right slot via
 *
 *     <PluginHeader title="..."> <template #actions> ... </template> </PluginHeader>
 */
export default {
  name: 'PluginHeader',
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    icon: { type: String, default: '' },
    height: { type: Number, default: 56 },
    // Optional override for the back button destination. By default
    // it navigates to /f/{farmId}; pass `backQuery` to tack on query
    // params (Variable Rate Mode preserves ?mypad=1).
    backQuery: { type: Object, default: null }
  },
  computed: {
    farmId () {
      return this.$store.state.farm && this.$store.state.farm.id
    },
    cssVars () {
      return { '--plugin-header-h': this.height + 'px' }
    }
  },
  methods: {
    goBack () {
      if (!this.farmId) {
        this.$router.push('/')
        return
      }
      const route = { path: '/f/' + this.farmId }
      if (this.backQuery) route.query = this.backQuery
      this.$router.push(route)
    }
  }
}
</script>

<style scoped>
.plugin-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--plugin-header-h, 56px);
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 14px;
  background: #ffffff;
  border-bottom: 1px solid #d6dbe0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  /* Brand accent stripe along the top so the chrome reads as
     Every.Farm even when the rest of the plugin's color palette
     varies. Same green used elsewhere in the platform. */
  background-image: linear-gradient(to right, #066739 0, #066739 100%);
  background-size: 100% 3px;
  background-position: 0 0;
  background-repeat: no-repeat;
  font-family: inherit;
}
.ph-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  border: 1px solid #d6dbe0;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 0.85rem;
  color: #2c3e50;
  cursor: pointer;
  transition: background 120ms ease, box-shadow 120ms ease,
              transform 120ms ease;
  white-space: nowrap;
}
.ph-back:hover {
  background: #f5f7f8;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  transform: translateY(-1px);
}
.ph-back:active { transform: translateY(0); }
.ph-back i { color: #066739; }
.ph-title-block {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1 1 auto;
}
.ph-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(6, 103, 57, 0.10);
  color: #066739;
  font-size: 0.95rem;
  flex: 0 0 auto;
}
.ph-title-text {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
  min-width: 0;
}
.ph-title {
  font-weight: 600;
  font-size: 1.02rem;
  color: #1f2d3a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ph-subtitle {
  font-size: 0.78rem;
  color: #6b7785;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ph-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}
@media (max-width: 600px) {
  .ph-back-label { display: none; }
  .ph-subtitle { display: none; }
}
</style>
